<!-- 该组件扩展自 el-image 组件，提供了图片的遮罩，badge 以及 description 的能力，目前专门提供给 list page 使用的 -->
<template>
    <div class="el-image" @mouseenter="showShadow()" @mouseleave="hideShadow()">
        <slot v-if="loading" name="placeholder">
            <div class="el-image__placeholder"></div>
        </slot>
        <slot v-else-if="error" name="error">
            <div class="el-image__error">{{ t('el.image.error') }}</div>
        </slot>
        <img
            v-else
            class="el-image__inner"
            v-bind="$attrs"
            v-on="$listeners"
            @click="clickHandler"
            :src="src"
            :style="[__imageStyle, imageStyle]"
            :class="{ 'el-image__inner--center': alignCenter, 'el-image__preview': preview }">
        <!-- 标识：追加的代码；给图片增加说明，当发生 hover 事件的时候，显示 descripiton slot 的内容。 -->
        <!-- 添加一个透明的遮罩 -->
        <div class="shadow" v-show="isShowShadow" v-if="!isMobileAgent">
            <div style="display: inline-flex; align-items: center; justify-content: center; height: 100%; ">
                <!-- the describe slot-->
                <slot name="desc"></slot>
            </div>
        </div>
        <!-- 添加 2: 插入 image badge（多图、视频的标识）-->
        <div style="position: absolute; top:0px; right:0px; padding: 5px 8px 0px 0px">
            <slot name="badge"></slot>
        </div>
        <template v-if="preview">
            <image-viewer :z-index="zIndex" :initial-index="imageIndex" v-if="showViewer" :on-close="closeViewer" :url-list="previewSrcList"/>
        </template>
    </div>
</template>

<script>
    import ImageViewer from 'element-ui/packages/image/src/image-viewer';
    import Locale from 'element-ui/src/mixins/locale';
    import { on, off, getScrollContainer, isInContainer } from 'element-ui/src/utils/dom';
    import { isString, isHtmlElement } from 'element-ui/src/utils/types';
    import throttle from 'throttle-debounce/throttle';
    import { isPhoneAgent, isMobileAgent } from "../../utils";

    const isSupportObjectFit = () => document.documentElement.style.objectFit !== undefined;

    const ObjectFit = {
        NONE: 'none',
        CONTAIN: 'contain',
        COVER: 'cover',
        FILL: 'fill',
        SCALE_DOWN: 'scale-down'
    };

    let prevOverflow = '';

    export default {
        name: 'ShadowBadgedImage',

        mixins: [Locale],
        inheritAttrs: false,

        components: {
            ImageViewer
        },

        props: {
            src: String,
            fit: String,
            lazy: Boolean,
            scrollContainer: {},
            previewSrcList: {
                type: Array,
                default: () => []
            },
            zIndex: {
                type: Number,
                default: 2000
            },
            imageStyle: Object
        },

        data() {
            return {
                loading: true,
                error: false,
                show: !this.lazy,
                imageWidth: 0,
                imageHeight: 0,
                showViewer: false,
                isShowShadow: false
            };
        },

        computed: {
            __imageStyle() {
                const { fit } = this;
                if (!this.$isServer && fit) {
                    return isSupportObjectFit()
                        ? { 'object-fit': fit }
                        : this.getImageStyle(fit);
                }
                return {};
            },
            alignCenter() {
                return !this.$isServer && !isSupportObjectFit() && this.fit !== ObjectFit.FILL;
            },
            preview() {
                const { previewSrcList } = this;
                return Array.isArray(previewSrcList) && previewSrcList.length > 0;
            },
            imageIndex() {
                let previewIndex = 0;
                const srcIndex = this.previewSrcList.indexOf(this.src);
                if (srcIndex >= 0) {
                    previewIndex = srcIndex;
                }
                return previewIndex;
            },
            isMobileAgent(){
                return isMobileAgent();
            }
        },

        watch: {
            src(val) {
                this.show && this.loadImage();
            },
            show(val) {
                val && this.loadImage();
            }
        },

        mounted() {
            if (this.lazy) {
                this.addLazyLoadListener();
            } else {
                this.loadImage();
            }
        },

        beforeDestroy() {
            this.lazy && this.removeLazyLoadListener();
        },

        methods: {
            loadImage() {
                if (this.$isServer) return;

                // reset status
                this.loading = true;
                this.error = false;

                const img = new Image();
                img.onload = e => this.handleLoad(e, img);
                img.onerror = this.handleError.bind(this);

                // bind html attrs
                // so it can behave consistently
                Object.keys(this.$attrs)
                    .forEach((key) => {
                        const value = this.$attrs[key];
                        img.setAttribute(key, value);
                    });
                img.src = this.src;
            },
            handleLoad(e, img) {
                this.imageWidth = img.width;
                this.imageHeight = img.height;
                this.loading = false;
                this.error = false;
            },
            handleError(e) {
                this.loading = false;
                this.error = true;
                this.$emit('error', e);
            },
            handleLazyLoad() {
                if (isInContainer(this.$el, this._scrollContainer)) {
                    this.show = true;
                    this.removeLazyLoadListener();
                }
            },
            addLazyLoadListener() {
                if (this.$isServer) return;

                const { scrollContainer } = this;
                let _scrollContainer = null;

                if (isHtmlElement(scrollContainer)) {
                    _scrollContainer = scrollContainer;
                } else if (isString(scrollContainer)) {
                    _scrollContainer = document.querySelector(scrollContainer);
                } else {
                    _scrollContainer = getScrollContainer(this.$el);
                }

                if (_scrollContainer) {
                    this._scrollContainer = _scrollContainer;
                    this._lazyLoadHandler = throttle(200, this.handleLazyLoad);
                    on(_scrollContainer, 'scroll', this._lazyLoadHandler);
                    this.handleLazyLoad();
                }
            },
            removeLazyLoadListener() {
                const { _scrollContainer, _lazyLoadHandler } = this;

                if (this.$isServer || !_scrollContainer || !_lazyLoadHandler) return;

                off(_scrollContainer, 'scroll', _lazyLoadHandler);
                this._scrollContainer = null;
                this._lazyLoadHandler = null;
            },
            /**
             * simulate object-fit behavior to compatible with IE11 and other browsers which not support object-fit
             */
            getImageStyle(fit) {
                const { imageWidth, imageHeight } = this;
                const {
                    clientWidth: containerWidth,
                    clientHeight: containerHeight
                } = this.$el;

                if (!imageWidth || !imageHeight || !containerWidth || !containerHeight) return {};

                const imageAspectRatio = imageWidth / imageHeight;
                const containerAspectRatio = containerWidth / containerHeight;

                if (fit === ObjectFit.SCALE_DOWN) {
                    const isSmaller = imageWidth < containerWidth && imageHeight < containerHeight;
                    fit = isSmaller ? ObjectFit.NONE : ObjectFit.CONTAIN;
                }

                switch (fit) {
                    case ObjectFit.NONE:
                        return { width: 'auto', height: 'auto' };
                    case ObjectFit.CONTAIN:
                        return (imageAspectRatio < containerAspectRatio) ? { width: 'auto' } : { height: 'auto' };
                    case ObjectFit.COVER:
                        return (imageAspectRatio < containerAspectRatio) ? { height: 'auto' } : { width: 'auto' };
                    default:
                        return {};
                }
            },

            clickHandler() {
                // don't show viewer when preview is false
                if (!this.preview) {
                    return;
                }
                // prevent body scroll
                prevOverflow = document.body.style.overflow;
                document.body.style.overflow = 'hidden';
                this.showViewer = true;
            },

            closeViewer() {
                document.body.style.overflow = prevOverflow;
                this.showViewer = false;
            },

            showShadow(){
                // 只有当不再手机端的时候，才需要 hover 事件，否则会因为 hover 需要双击才能实现点击动作
                if(!isMobileAgent()){
                    this.isShowShadow = true;
                }
            },

            hideShadow(){
                // 只有当不再手机端的时候，才需要 hover 事件，否则会因为 hover 需要双击才能实现点击动作
                if(!isMobileAgent()){
                    this.isShowShadow = false;
                }
            }
        }
    };
</script>

<style>
    /* PC Screen */
    @media (min-width: 736px) {
        /*
         z-index 值的设置比较的关键，经历过如下的版本设置
           v1: z-index = 20，超过了 header 的 z-index 19 导致 list 页面中的遮罩会遮住 header，导致展示上的 bug（另外 header 的 z-index
               设置为 19 的原因是，它必须被 popup entity window 所覆盖，而 popup entity window 目前的 z-index = 20。
           v2: 为了解决 v1 的 bug，将 z-index 设置为了 18
           v3: 后来新增了 backtop 箭头，发现，它的 z-index 值为 5，在小尺寸的屏幕下，当 backtop arrow 与 list page 重叠后，会导致被 ShadowBagedImage
               的阴影所覆盖。因此，将 ShadowBagedImage 阴影的 z-index 即下面的 z-index 设置为了 4.
         */
        .el-image .shadow {
            position: absolute;
            top: 0px;
            width: 100%;
            height: 100%;
            z-index: 4; /* 注意，这个值不能设置得过高，不然会遮挡部分 popup popupingEntity window，header 的 z-index 是 19 不能设置得比它高，否则就会出现阴影遮住 header 的 bug */
            /*background-color: #303133;*/
            background-color: black;
            opacity: 0.52;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/commons/ShadowBadgedImage.vue